import {
  IBlogContentBlockApi,
  IBlogPostDetail,
  IBlogPostListItem,
  IBlogPostStorePayload,
} from '@/models/blog-post.model';
import { IContentBlockInstance, IContentPage } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';

const ALL_LOCALES: SupportedLocales[] = [SupportedLocales.UK, SupportedLocales.RU];

const BLOG_LOCALE_TEXT_MAX = 255;

export const resolveActiveLocale = (lang: string): SupportedLocales => {
  const base = lang.split('-')[0].toLowerCase();
  if (base === SupportedLocales.RU) return SupportedLocales.RU;
  return SupportedLocales.UK;
};

export const makeBlockInstanceId = (position: number, type: string): string => `block-${position}-${type}`;

export function mapApiContentToBlocks(content: IBlogContentBlockApi[] | null | undefined): IContentBlockInstance[] {
  if (!content?.length) return [];
  const sorted = [...content].sort((a, b) => a.position - b.position);
  return sorted.map((b, index) => ({
    id: makeBlockInstanceId(index, b.type),
    type: b.type,
    data:
      b.type === 'product_slider' && b.data && typeof b.data === 'object' && !Array.isArray(b.data)
        ? (() => {
            const raw = b.data as Record<string, unknown>;
            const uuids = Array.isArray(raw.uuids) ? (raw.uuids as string[]) : [];
            const legacySkus = Array.isArray(raw.skus) ? (raw.skus as string[]) : [];
            const normalized = uuids.length > 0 ? uuids : legacySkus;
            return {
              ...raw,
              uuids: normalized,
            };
          })()
        : b.data,
  }));
}

export function mapBlocksToApiContent(
  blocks: IContentBlockInstance[],
  locale: SupportedLocales,
): IBlogContentBlockApi[] {
  const isLocaleStringMap = (value: unknown): value is Record<SupportedLocales, string> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    if (!keys.length) return false;
    return keys.every((k) => ALL_LOCALES.includes(k as SupportedLocales) && typeof obj[k] === 'string');
  };

  const isLocaleStringArrayMap = (value: unknown): value is Record<SupportedLocales, string[]> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    if (!keys.length) return false;
    return keys.every(
      (k) =>
        ALL_LOCALES.includes(k as SupportedLocales) &&
        Array.isArray(obj[k]) &&
        (obj[k] as unknown[]).every((item) => typeof item === 'string'),
    );
  };

  const normalizeNestedValueForLocale = (value: unknown, targetLocale: SupportedLocales): unknown => {
    if (isLocaleStringMap(value)) {
      return value[targetLocale] ?? '';
    }
    if (isLocaleStringArrayMap(value)) {
      return value[targetLocale] ? [...value[targetLocale]] : [];
    }
    if (Array.isArray(value)) {
      return value.map((item) => normalizeNestedValueForLocale(item, targetLocale));
    }
    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, nested]) => {
        acc[key] = normalizeNestedValueForLocale(nested, targetLocale);
        return acc;
      }, {});
    }
    return value;
  };

  const normalizeBlockData = (value: unknown, targetLocale: SupportedLocales): Record<string, unknown> | unknown[] => {
    if (Array.isArray(value)) {
      const normalizedArray = value.map((item) => normalizeNestedValueForLocale(item, targetLocale));
      return normalizedArray.length > 0 ? normalizedArray : [{ _empty: true }];
    }
    if (value && typeof value === 'object') {
      const obj = normalizeNestedValueForLocale(value, targetLocale) as Record<string, unknown>;
      return Object.keys(obj).length > 0 ? obj : { _empty: true };
    }
    return { _empty: true };
  };

  return blocks
    .filter((b): b is IContentBlockInstance => Boolean(b && typeof b.type === 'string'))
    .map((b, position) => ({
      type: b.type,
      position,
      data:
        b.type === 'product_slider' && b.data && typeof b.data === 'object' && !Array.isArray(b.data)
          ? (() => {
              const normalized = normalizeBlockData(b.data, locale) as Record<string, unknown>;
              const uuids = Array.isArray(normalized.uuids)
                ? (normalized.uuids as string[])
                : Array.isArray(normalized.skus)
                ? (normalized.skus as string[])
                : [];
              const payloadData = { ...normalized };
              delete payloadData.skus;
              return {
                ...payloadData,
                uuids,
              };
            })()
          : normalizeBlockData(b.data, locale),
    }));
}

export function mapBlogDetailToContentPage(detail: IBlogPostDetail, activeLocale: SupportedLocales): IContentPage {
  const localeSource = detail.locale ?? {};
  const localeMeta: Record<string, { name: string; slug: string; description: string }> = {};
  const localeBlocks: Record<string, IContentBlockInstance[]> = {};

  ALL_LOCALES.forEach((loc) => {
    const row = localeSource[loc];
    localeMeta[loc] = {
      name: row?.name ?? '',
      slug: row?.slug ?? '',
      description: row?.description ?? '',
    };
    localeBlocks[loc] = mapApiContentToBlocks(row?.content ?? null);
  });

  const meta = localeMeta[activeLocale] ?? localeMeta[SupportedLocales.UK];

  return {
    id: detail.blog_post_id ?? detail.id,
    pageType: 'blog',
    title: meta.name,
    slug: '',
    tags: [],
    blocks: localeBlocks[activeLocale] ?? [],
    published: Boolean(detail.is_published),
    isDraft: Boolean(detail.is_draft),
    isPinned: detail.is_pinned,
    coverImage: detail.cover_image ?? null,
    publishedAt: detail.published_at ?? null,
    categoryId: detail.category?.id ?? null,
    blogCategoryId: detail.blog_category?.id ?? null,
    localeMeta,
    localeBlocks,
  };
}

export function buildBlogPostStorePayload(page: IContentPage): IBlogPostStorePayload {
  const localeMeta = page.localeMeta ?? {};
  const localeBlocks = page.localeBlocks ?? {};

  const locale: IBlogPostStorePayload['locale'] = {};

  ALL_LOCALES.forEach((loc) => {
    const meta = localeMeta[loc] ?? { name: '', slug: '', description: '' };
    const blocks = Array.isArray(localeBlocks[loc]) ? localeBlocks[loc] : [];
    const fallbackName = (page.title || '').trim() || 'Draft';
    const normalizedName = (meta.name || '').trim() || fallbackName;
    const descriptionTrimmed = (meta.description || '').trim();
    const titleForAlt = normalizedName.slice(0, BLOG_LOCALE_TEXT_MAX);
    locale[loc] = {
      name: normalizedName.slice(0, BLOG_LOCALE_TEXT_MAX),
      slug: null,
      description: descriptionTrimmed ? descriptionTrimmed.slice(0, BLOG_LOCALE_TEXT_MAX) : null,
      cover_image_alt: titleForAlt || null,
      content: mapBlocksToApiContent(blocks, loc),
    };
  });

  return {
    category_id: page.categoryId ?? null,
    blog_category_id: page.blogCategoryId ?? null,
    cover_image: page.coverImage ?? null,
    is_pinned: Boolean(page.isPinned),
    locale,
  };
}

export function createEmptyBlogContentPage(activeLocale: SupportedLocales, routeId: string): IContentPage {
  const localeMeta: Record<string, { name: string; slug: string; description: string }> = {
    [SupportedLocales.UK]: {
      name: '',
      slug: '',
      description: '',
    },
    [SupportedLocales.RU]: {
      name: '',
      slug: '',
      description: '',
    },
  };
  const localeBlocks: Record<string, IContentBlockInstance[]> = {
    [SupportedLocales.UK]: [],
    [SupportedLocales.RU]: [],
  };

  const meta = localeMeta[activeLocale];

  return {
    id: routeId,
    pageType: 'blog',
    title: meta.name,
    slug: '',
    tags: [],
    blocks: localeBlocks[activeLocale] ?? [],
    published: false,
    isDraft: true,
    isPinned: false,
    coverImage: null,
    publishedAt: null,
    categoryId: null,
    blogCategoryId: null,
    localeMeta,
    localeBlocks,
  };
}

export function getListItemTitle(item: IBlogPostListItem, lang: string): string {
  const code = lang.split('-')[0];
  const fromCode = item.locale?.[code];
  if (fromCode?.name) return fromCode.name;
  const uk = item.locale?.[SupportedLocales.UK]?.name;
  if (uk) return uk;
  const first = Object.values(item.locale ?? {})[0]?.name;
  return first ?? '—';
}

export function getListItemSlug(item: IBlogPostListItem, lang: string): string {
  const code = lang.split('-')[0];
  const fromCode = item.locale?.[code];
  if (fromCode?.slug) return fromCode.slug;
  const uk = item.locale?.[SupportedLocales.UK]?.slug;
  if (uk) return uk;
  const first = Object.values(item.locale ?? {})[0]?.slug;
  return first ?? '';
}
